/**
 * PhotoGrid — drag-to-reorder, async upload, pending cards, approved cards.
 * Uses @dnd-kit for drag-and-drop sortability.
 */

import { useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { AlertCircle, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { PhotoCard } from './PhotoCard';
import { PendingPhotoCard } from './PendingPhotoCard';
import { useUnitPhotos } from '../../hooks/useUnitPhotos';
import type { UnitPhoto } from '../../types/unit-marketing.types';

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/heic';
const MAX_APPROVED = 20;

interface Props {
  unitId: string;
}

export function PhotoGrid({ unitId }: Props) {
  const {
    photos,
    pendingPhotos,
    isLoading,
    uploadError,
    fetchError,
    rejectionMessage,
    clearRejectionMessage,
    uploadPhotos,
    deletePhoto,
    reorderPhotos,
    updateCaption,
    setPrimaryPhoto,
    refetchPhotos,
  } = useUnitPhotos(unitId);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [skippedMessage, setSkippedMessage] = useState<string | null>(null);

  const approvedPhotos = photos.filter((p: UnitPhoto) => p.status === 'approved');
  const canUpload = approvedPhotos.length < MAX_APPROVED;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = approvedPhotos.findIndex(p => p.id === active.id);
    const newIndex = approvedPhotos.findIndex(p => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Build new ordered array
    const reordered = [...approvedPhotos];
    const spliced = reordered.splice(oldIndex, 1);
    const moved = spliced[0];
    if (!moved) return;
    reordered.splice(newIndex, 0, moved);
    const orderedIds = reordered.map(p => p.id);

    await reorderPhotos(orderedIds);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset input so the same file(s) can be selected again if needed
    e.target.value = '';
    if (files.length === 0) return;
    setSkippedMessage(null);
    const maxAdditional = MAX_APPROVED - approvedPhotos.length;
    const skipped = await uploadPhotos(files, maxAdditional);
    if (skipped > 0) {
      setSkippedMessage(`${skipped} photo(s) were not uploaded — the 20-photo limit was reached.`);
    }
  };

  if (isLoading) {
    return (
      <div data-testid="unit-photo-grid">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Skeleton className="rounded-lg" style={{ aspectRatio: '4/3' }} />
          <Skeleton className="rounded-lg" style={{ aspectRatio: '4/3' }} />
          <Skeleton className="rounded-lg" style={{ aspectRatio: '4/3' }} />
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div
        data-testid="unit-photo-grid"
        className="flex flex-col items-center justify-center py-10 rounded-lg border border-destructive/20 bg-destructive/5 text-center gap-3"
        role="alert"
      >
        <AlertCircle className="h-6 w-6 text-destructive" aria-hidden="true" />
        <p className="text-sm text-destructive">{fetchError}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refetchPhotos()}
          data-testid="photo-grid-retry"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="unit-photo-grid">
      {/* Rejection notification */}
      {rejectionMessage && (
        <div className="mb-3 flex items-start gap-3 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="flex-1 text-sm text-destructive">{rejectionMessage}</p>
          <button
            type="button"
            onClick={clearRejectionMessage}
            className="text-xs text-destructive font-medium hover:underline shrink-0"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div
          role="alert"
          data-testid="photo-upload-error"
          className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg"
        >
          <p className="flex-1 text-sm text-destructive">{uploadError}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-[44px]"
            data-testid="photo-upload-retry"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose photos again
          </Button>
        </div>
      )}

      {/* Skipped-upload notice (photo limit reached mid-selection) */}
      {skippedMessage && (
        <div className="mb-3 flex items-start gap-3 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="flex-1 text-sm text-destructive">{skippedMessage}</p>
          <button
            type="button"
            onClick={() => setSkippedMessage(null)}
            className="text-xs text-destructive font-medium hover:underline shrink-0"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        className="hidden"
        onChange={handleFileChange}
        aria-label="Upload photo"
      />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={approvedPhotos.map(p => p.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {approvedPhotos.map(photo => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                onDelete={deletePhoto}
                onUpdateCaption={updateCaption}
                isPrimary={photo.id === approvedPhotos[0]?.id}
                onSetPrimary={setPrimaryPhoto}
              />
            ))}
            {pendingPhotos.map(pending => (
              <PendingPhotoCard key={pending.tempId} localPreviewUrl={pending.localPreviewUrl} />
            ))}
            {canUpload && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="photo-upload-button"
                className="col-span-2 flex min-h-[96px] flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-input px-3 py-4 text-sm font-medium text-foreground hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:col-span-1 sm:aspect-[4/3] sm:min-h-0"
              >
                <Plus className="h-5 w-5" aria-hidden="true" />
                Add photos
                <span className="text-xs font-normal text-muted-foreground">JPEG, PNG, WebP or HEIC</span>
              </button>
            )}
          </div>
        </SortableContext>
      </DndContext>

      {approvedPhotos.length === 0 && pendingPhotos.length === 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          No photos yet. Photos are reviewed before they appear on the listing.
        </p>
      )}

      {!canUpload && (
        <p className="mt-2 text-xs text-muted-foreground">
          Maximum of {MAX_APPROVED} approved photos reached. Delete a photo to upload a new one.
        </p>
      )}
    </div>
  );
}
